import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Lightning, Minus, Plus, ArrowCounterClockwise, LockSimple } from "@phosphor-icons/react";
import { Slider } from "../components/ui/slider";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { Header, Disclaimer } from "../components/Header";
import { ParlayCard } from "../components/ParlayCard";
import { PaywallDialog } from "../components/PaywallDialog";

const SPORTS = [
  { key: "nfl", name: "NFL", img: "https://images.unsplash.com/photo-1544765773-a8dce1f272f6?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { key: "nba", name: "NBA", img: "https://images.unsplash.com/photo-1608245449230-4ac19066d2d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { key: "mlb", name: "MLB", img: "https://images.unsplash.com/photo-1529768167801-9173d94c2a42?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { key: "nhl", name: "NHL", img: "https://images.unsplash.com/flagged/photo-1550585477-a025700d7fce?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
];

const RISKS = [
  { key: "safe", label: "Safe", color: "#00E676", desc: "High probability legs, modest payout" },
  { key: "medium", label: "Medium", color: "#F59E0B", desc: "Balanced probability and payout" },
  { key: "lotto", label: "Lotto", color: "#FF3366", desc: "Long shots, massive payout potential" },
];

const riskFromSlider = (v) => (v <= 33 ? 0 : v <= 66 ? 1 : 2);
const RISK_INIT = { safe: 15, medium: 50, lotto: 85 };

const timeAgo = (iso) => {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  return `${Math.round(mins / 60)}h ago`;
};

export default function Builder() {
  const { user, API } = useAuth();
  const favAllowed = user?.plan === "pro" || user?.plan === "amateur" || ["nfl", "nba"].includes(user?.favorite_sport);
  const [sport, setSport] = useState(favAllowed && user?.favorite_sport ? user.favorite_sport : "nfl");
  const [sliderVal, setSliderVal] = useState(RISK_INIT[user?.risk_preference] ?? 50);
  const [numLegs, setNumLegs] = useState(3);
  const [loading, setLoading] = useState(false);
  const [parlays, setParlays] = useState(null);
  const [linesInfo, setLinesInfo] = useState(null);
  const [savedIds, setSavedIds] = useState([]);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallMsg, setPaywallMsg] = useState("");

  const isPaid = user?.plan === "pro" || user?.plan === "amateur";
  const risk = RISKS[riskFromSlider(sliderVal)];

  const pickSport = (key) => {
    if (!isPaid && !["nfl", "nba"].includes(key)) {
      setPaywallMsg("MLB and NHL are for paid members.");
      setShowPaywall(true);
      return;
    }
    setSport(key);
  };

  const generate = async () => {
    setLoading(true);
    setParlays(null);
    try {
      const { data } = await axios.post(`${API}/parlays/generate`,
        { sport, risk: risk.key, num_legs: numLegs }, { withCredentials: true });
      setParlays(data.parlays);
      setLinesInfo(data.lines || null);
    } catch (err) {
      if (err.response?.status === 402) {
        setPaywallMsg(formatApiErrorDetail(err.response?.data?.detail));
        setShowPaywall(true);
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Generation failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const save = async (parlay) => {
    try {
      await axios.post(`${API}/parlays/save`, { parlay }, { withCredentials: true });
      setSavedIds((s) => [...s, parlay.id]);
      toast.success("Parlay saved to your history");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-slate-50 noise-bg">
      <Header />
      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#00E676] mb-2">Parlay Builder</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter mb-10">
          {user?.name ? `Let's cook, ${user.name.split(" ")[0]}` : "Let's cook"}
        </h1>

        <section className="mb-10">
          <h2 className="text-base md:text-lg font-bold uppercase tracking-wide text-slate-300 mb-4">1 · Pick your sport</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {SPORTS.map((s) => {
              const locked = !isPaid && !["nfl", "nba"].includes(s.key);
              return (
              <button key={s.key} onClick={() => pickSport(s.key)} data-testid={`sport-tile-${s.key}`}
                className={`relative h-28 sm:h-36 rounded-xl overflow-hidden text-left transition-transform hover:scale-[1.02] active:scale-95 ${
                  sport === s.key ? "ring-2 ring-[#2563EB] scale-[1.02]" : "ring-1 ring-white/10"}`}>
                <img src={s.img} alt={s.name} className={`absolute inset-0 w-full h-full object-cover ${locked ? "grayscale opacity-50" : ""}`} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                <span className="absolute bottom-2.5 left-3.5 font-display text-2xl font-black uppercase tracking-tight">{s.name}</span>
                {locked && (
                  <span data-testid={`sport-locked-${s.key}`}
                    className="absolute top-2.5 right-3 flex items-center gap-1 rounded-full bg-black/70 border border-[#FFD700]/40 text-[#FFD700] px-2 py-0.5 text-[10px] font-black uppercase">
                    <LockSimple size={11} weight="bold" /> Pro
                  </span>
                )}
                {sport === s.key && !locked && <span className="absolute top-2.5 right-3 h-2.5 w-2.5 rounded-full bg-[#2563EB] drop-shadow-[0_0_6px_rgba(37,99,235,0.8)]" />}
              </button>
              );
            })}
          </div>
        </section>

        <div className="grid md:grid-cols-2 gap-8 mb-10">
          <section className="bg-[#141419] border border-white/10 rounded-xl p-6">
            <h2 className="text-base md:text-lg font-bold uppercase tracking-wide text-slate-300 mb-1">2 · Risk level</h2>
            <p className="font-display text-3xl font-black uppercase tracking-tight mb-1" style={{ color: risk.color }} data-testid="risk-label">
              {risk.label}
            </p>
            <p className="text-xs text-slate-500 mb-6">{risk.desc}</p>
            <Slider value={[sliderVal]} onValueChange={(v) => setSliderVal(v[0])} max={100} step={1}
              data-testid="risk-slider"
              className="[&_[role=slider]]:h-7 [&_[role=slider]]:w-7 [&_[role=slider]]:border-2 [&>span:first-child]:h-3" />
            <div className="flex justify-between mt-3 text-[11px] font-bold uppercase tracking-wider">
              <span className="text-[#00E676]">Safe</span>
              <span className="text-[#F59E0B]">Medium</span>
              <span className="text-[#FF3366]">Lotto</span>
            </div>
          </section>

          <section className="bg-[#141419] border border-white/10 rounded-xl p-6">
            <h2 className="text-base md:text-lg font-bold uppercase tracking-wide text-slate-300 mb-1">3 · Number of legs</h2>
            <p className="text-xs text-slate-500 mb-6">More legs = bigger payout, lower odds of hitting</p>
            <div className="flex items-center justify-center gap-6">
              <button onClick={() => setNumLegs((n) => Math.max(2, n - 1))} data-testid="legs-minus-btn"
                className="h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center">
                <Minus size={20} weight="bold" />
              </button>
              <span className="font-display text-6xl font-black w-20 text-center" data-testid="legs-count">{numLegs}</span>
              <button onClick={() => setNumLegs((n) => Math.min(8, n + 1))} data-testid="legs-plus-btn"
                className="h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center">
                <Plus size={20} weight="bold" />
              </button>
            </div>
            <p className="text-center text-xs text-slate-500 mt-4">2 – 8 legs</p>
          </section>
        </div>

        <button onClick={generate} disabled={loading} data-testid="generate-parlays-btn"
          className="w-full sm:w-auto rounded-full bg-[#00E676] text-black font-bold px-10 py-4 text-base hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60 inline-flex items-center justify-center gap-2">
          <Lightning size={20} weight="fill" />
          {loading ? "Crunching the numbers..." : "Generate 3 parlays"}
        </button>

        <AnimatePresence>
          {loading && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="mt-10 text-center py-16" data-testid="generation-loading">
              <div className="inline-block h-10 w-10 border-2 border-[#00E676] border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-slate-400 text-sm">Removing vig · ranking EV · writing coach's notes...</p>
            </motion.div>
          )}
        </AnimatePresence>

        {parlays && (
          <section className="mt-12">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-display text-3xl font-black uppercase tracking-tight">Your tickets</h2>
                {linesInfo && (
                  <p className="text-xs font-semibold mt-1 flex items-center gap-1.5" data-testid="lines-updated">
                    {linesInfo.source === "live" ? (
                      <>
                        <span className="h-1.5 w-1.5 rounded-full bg-[#00E676] animate-pulse" />
                        <span className="text-slate-400">Live market lines · updated {timeAgo(linesInfo.updated_at)}</span>
                      </>
                    ) : (
                      <span className="text-slate-500">Simulated lines (demo data)</span>
                    )}
                  </p>
                )}
              </div>
              <button onClick={generate} data-testid="regenerate-btn"
                className="flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-white transition-colors">
                <ArrowCounterClockwise size={16} weight="bold" /> Regenerate
              </button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="parlay-results">
              {parlays.map((p, i) => (
                <ParlayCard key={p.id} parlay={p} index={i} onSave={save} saved={savedIds.includes(p.id)} />
              ))}
            </div>
          </section>
        )}
      </main>
      <PaywallDialog open={showPaywall} onOpenChange={setShowPaywall} message={paywallMsg} API={API} />
      <Disclaimer />
    </div>
  );
}
